import { useState } from 'react';
import { Button, EmptyState, InlineError, Skeleton } from '../../components/ui';
import { ReconnectDriveButton, useDriveConnection } from '../auth/ReconnectDrive';
import { useAuth } from '../auth/AuthProvider';
import { useTapPeople } from '../projects/hooks';
import { readAppEnv } from '../../lib/env';
import { ensureProjectFolder, uploadFileToDrive, withDriveToken } from './drive';
import {
  assetsInFolder,
  attribution,
  deleteFolderWarning,
  findFolderByName,
  folderCounts,
  folderNameProblem,
  folderUploadFiles,
  mayDeleteFolder,
  mayRemoveSharedAsset,
  sortFolders,
  topFolderName,
  UNFILED_ID,
  uploadFailureMessage,
} from './resourceLinks';
import type { SharedAsset, SharedAssetFolder } from './resourceLinks';
import {
  resourceErrorMessage,
  useCreateSharedAssetFolder,
  useDeleteSharedAsset,
  useDeleteSharedAssetFolder,
  useMoveSharedAsset,
  useRecordSharedAsset,
  useSharedAssetFolders,
  useSharedAssets,
} from './resourceQueries';

/**
 * Standalone team files: logos, brand guidelines, templates - the things that
 * belong to everybody and to no task.
 *
 * The folder name is deliberately not something a project could be called.
 * ensureProjectFolder matches by NAME under the Drive root, so a project
 * called "Shared Assets" would quietly share this folder; the underscore
 * keeps it out of reach of a real project name.
 */
const SHARED_FOLDER_NAME = '_TAP Shared Assets';

function sizeLabel(bytes: number | null): string {
  if (bytes === null) {
    return '';
  }
  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024)).toString()} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function SharedAssetLibrary() {
  const { client, access } = useAuth();
  const assets = useSharedAssets();
  const folders = useSharedAssetFolders();
  const people = useTapPeople(true);
  const record = useRecordSharedAsset();
  const createFolder = useCreateSharedAssetFolder();
  const removeFolder = useDeleteSharedAssetFolder();
  const drive = useDriveConnection();
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [openFolderId, setOpenFolderId] = useState(UNFILED_ID);
  const [newFolder, setNewFolder] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  if (assets.isPending || folders.isPending) {
    return <Skeleton lines={3} label="Loading shared assets" />;
  }
  if (assets.isError || folders.isError) {
    return <InlineError>Shared assets could not be loaded.</InlineError>;
  }

  const rows = assets.data ?? [];
  const folderRows = sortFolders(folders.data ?? []);
  const openFolder = folderRows.find((folder) => folder.id === openFolderId);
  const visibleRows = assetsInFolder(rows, openFolderId === UNFILED_ID ? null : openFolderId);
  const counts = folderCounts(rows);
  const viewer = access.status === 'approved' ? access.profile : null;
  const nameById = new Map(
    (people.data ?? []).map((person) => [
      person.id,
      person.display_name === '' ? person.email : person.display_name,
    ]),
  );

  // Most of the team has never connected Drive. Offering an upload control
  // that can only fail is worse than saying plainly what is missing.
  const driveReady = drive.data === true;

  async function createAtDrive(name: string): Promise<SharedAssetFolder | null> {
    const problem = folderNameProblem(name, folderRows);
    if (problem !== null) {
      setError(problem);
      return null;
    }
    const rootFolderId = readAppEnv().driveRootFolderId;
    if (rootFolderId === null) {
      setError('Drive is not configured for this environment.');
      return null;
    }
    try {
      const driveFolderId = await withDriveToken(client, async (token) => {
        const shared = await ensureProjectFolder(token, rootFolderId, SHARED_FOLDER_NAME);
        return ensureProjectFolder(token, shared, name.trim());
      });
      return await createFolder.mutateAsync({ name, driveFolderId });
    } catch (cause) {
      setError(resourceErrorMessage(cause instanceof Error ? cause.message : ''));
      return null;
    }
  }

  // One file at a time, in the order picked. Parallel uploads would race
  // ensureProjectFolder (it creates the folder when its search finds none)
  // and could leave two "_TAP Shared Assets" folders. Folder uploads use this
  // same loop. A failed file does not stop the rest; one error line afterwards
  // names every file that did not go up.
  async function upload(
    files: File[],
    target: { folderId: string | null; driveFolderId: string | null },
  ) {
    const rootFolderId = readAppEnv().driveRootFolderId;
    if (rootFolderId === null) {
      setError('Drive is not configured for this environment.');
      return;
    }
    setError('');
    setUploading(true);
    const failed: string[] = [];
    try {
      for (const file of files) {
        try {
          const uploaded = await withDriveToken(client, async (token) => {
            const parent =
              target.driveFolderId ??
              (await ensureProjectFolder(token, rootFolderId, SHARED_FOLDER_NAME));
            return uploadFileToDrive(token, parent, file);
          });
          await record.mutateAsync({
            name: uploaded.name,
            url: uploaded.webViewLink ?? `https://drive.google.com/file/d/${uploaded.id}/view`,
            driveFileId: uploaded.id,
            mimeType: uploaded.mimeType ?? null,
            sizeBytes: uploaded.size === undefined ? null : Number(uploaded.size),
            ...(target.folderId === null ? {} : { folderId: target.folderId }),
          });
        } catch {
          failed.push(file.name);
        }
      }
    } finally {
      setUploading(false);
    }
    setError(uploadFailureMessage(failed));
  }

  async function uploadFolder(picked: File[]) {
    const files = folderUploadFiles(picked);
    const name = topFolderName(picked);
    if (name === '') {
      setError('That folder could not be read. Try Upload files instead.');
      return;
    }
    let folder = findFolderByName(folderRows, name);
    if (folder === undefined) folder = (await createAtDrive(name)) ?? undefined;
    if (folder === undefined) return;
    setOpenFolderId(folder.id);
    await upload(files, { folderId: folder.id, driveFolderId: folder.drive_folder_id });
  }

  return (
    <div>
      {error === '' ? null : <InlineError>{error}</InlineError>}
      <div className="resource-toolbar">
        <div>
          <p className="resource-toolbar-note">
            Files that belong to the whole team rather than a task: logos, brand guidelines,
            templates.
          </p>
          <ul className="resource-folder-list" aria-label="Folders">
            <li>
              <button
                className="resource-folder-button"
                aria-pressed={openFolderId === UNFILED_ID}
                onClick={() => setOpenFolderId(UNFILED_ID)}
              >
                Unfiled ({counts.get(UNFILED_ID) ?? 0})
              </button>
            </li>
            {folderRows.map((folder) => (
              <li key={folder.id}>
                <button
                  className="resource-folder-button"
                  aria-pressed={openFolderId === folder.id}
                  onClick={() => setOpenFolderId(folder.id)}
                >
                  {folder.name} ({counts.get(folder.id) ?? 0})
                </button>
              </li>
            ))}
          </ul>
        </div>
        {driveReady ? (
          <>
            <label className="ui-button ui-button--secondary ui-button--sm">
              {uploading ? 'Uploading...' : 'Upload files'}
              <input
                type="file"
                multiple
                aria-label="Upload shared assets"
                style={{ display: 'none' }}
                disabled={uploading}
                onChange={(event) => {
                  const files = Array.from(event.target.files ?? []);
                  event.target.value = '';
                  if (files.length > 0)
                    void upload(files, {
                      folderId: openFolder?.id ?? null,
                      driveFolderId: openFolder?.drive_folder_id ?? null,
                    });
                }}
              />
            </label>
            <label className="ui-button ui-button--secondary ui-button--sm">
              Upload a folder
              <input
                ref={(input) => input?.setAttribute('webkitdirectory', '')}
                type="file"
                multiple
                aria-label="Upload a folder"
                style={{ display: 'none' }}
                disabled={uploading}
                onChange={(event) => {
                  const files = Array.from(event.target.files ?? []);
                  event.target.value = '';
                  if (files.length > 0) void uploadFolder(files);
                }}
              />
            </label>
            <Button variant="ghost" size="sm" onClick={() => setNewFolder(true)}>
              New folder
            </Button>
          </>
        ) : (
          <ReconnectDriveButton />
        )}
      </div>
      {driveReady ? (
        <p className="resource-toolbar-note">
          Upload a folder puts every file in it, including files in its subfolders, into one Hub
          folder.
        </p>
      ) : null}
      {newFolder ? (
        <div className="resource-new-category">
          <input
            aria-label="Folder name"
            value={folderName}
            onChange={(event) => setFolderName(event.target.value)}
          />
          <Button
            size="sm"
            onClick={() => {
              void createAtDrive(folderName).then((folder) => {
                if (folder !== null) {
                  setOpenFolderId(folder.id);
                  setFolderName('');
                  setNewFolder(false);
                }
              });
            }}
          >
            Create folder
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setNewFolder(false)}>
            Cancel
          </Button>
        </div>
      ) : null}
      {openFolder !== undefined && mayDeleteFolder(viewer) ? (
        <div className="resource-row-actions">
          {confirmingDelete ? (
            <>
              <span>{deleteFolderWarning(openFolder, counts.get(openFolder.id) ?? 0)}</span>
              <Button
                variant="danger"
                size="sm"
                disabled={removeFolder.isPending}
                onClick={() =>
                  removeFolder.mutate(openFolder.id, {
                    onSuccess: () => {
                      setOpenFolderId(UNFILED_ID);
                      setConfirmingDelete(false);
                    },
                    onError: (cause) => setError(resourceErrorMessage(cause.message)),
                  })
                }
              >
                Confirm delete folder
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirmingDelete(false)}>
                Keep
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Delete folder ${openFolder.name}`}
              onClick={() => setConfirmingDelete(true)}
            >
              Delete folder
            </Button>
          )}
        </div>
      ) : null}
      {!driveReady ? (
        <EmptyState
          title="Connect Google Drive to upload"
          description="Shared assets live in the team's Drive folder, so uploading needs your Drive connected once. You can still open everything already here."
        />
      ) : null}
      {visibleRows.length === 0 ? (
        <EmptyState
          title="No files in this folder yet"
          description="Upload the files this client needs."
        />
      ) : (
        <ul className="resource-card-grid">
          {visibleRows.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              folders={folderRows}
              nameById={nameById}
              onError={setError}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * One asset card. At module scope so each card keeps its own "are you sure?"
 * state across the list's re-renders; same shape as LinkCard in
 * ResourceDirectory. Delete removes the Hub row only: the file stays in the
 * Drive folder, so a mistake can be undone from Drive.
 */
function AssetCard({
  asset,
  folders,
  nameById,
  onError,
}: {
  asset: SharedAsset;
  folders: SharedAssetFolder[];
  nameById: Map<string, string>;
  onError: (message: string) => void;
}) {
  const { access } = useAuth();
  const remove = useDeleteSharedAsset();
  const move = useMoveSharedAsset();
  const [confirming, setConfirming] = useState(false);
  const viewer = access.status === 'approved' ? access.profile : null;
  return (
    <li className="resource-card">
      <a className="resource-card-title" href={asset.url} target="_blank" rel="noreferrer">
        {asset.name}
      </a>
      <span className="resource-card-host">{sizeLabel(asset.size_bytes)}</span>
      <span className="resource-card-meta">
        {attribution(nameById.get(asset.uploaded_by) ?? '', asset.created_at)}
      </span>
      {mayRemoveSharedAsset(viewer, asset) ? (
        <>
          <label>
            Move to folder
            <select
              aria-label={`Move ${asset.name} to folder`}
              value={asset.folder_id ?? UNFILED_ID}
              onChange={(event) =>
                move.mutate(
                  {
                    assetId: asset.id,
                    folderId: event.target.value === UNFILED_ID ? null : event.target.value,
                  },
                  { onError: (cause) => onError(resourceErrorMessage(cause.message)) },
                )
              }
            >
              <option value={UNFILED_ID}>Unfiled</option>
              {folders.map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.name}
                </option>
              ))}
            </select>
          </label>
          <div className="resource-row-actions">
            {confirming ? (
              <>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={remove.isPending}
                  onClick={() => {
                    onError('');
                    remove.mutate(asset.id, {
                      onError: (cause) => onError(resourceErrorMessage(cause.message)),
                    });
                  }}
                >
                  Confirm delete
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                  Keep
                </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Delete ${asset.name}`}
                onClick={() => setConfirming(true)}
              >
                Delete
              </Button>
            )}
          </div>
        </>
      ) : null}
    </li>
  );
}
